import { useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { SubmitHandler, useForm } from 'react-hook-form';

import mailImg from '../../../assets/auth/mail.svg';
import { Button, Input } from '../../../components';
import {
  useGetAuthConfigurationQuery,
  useLoginMutation,
} from '../../../services';
import { Login as LoginSession, LoginData } from '../../../models';
// core-api answers a FAILED login with HTTP 200 and a non-'0' errorCode body
// (e.g. `{ errorCode: '1027', message: 'INVALID_CREDINTIALS', data: '' }`), so
// `.unwrap()` does NOT reject on it. The previous handler unwrapped that reply,
// destructured accessToken/refreshToken/userName out of the `''`, persisted the
// literal string 'undefined' as the session and navigated to '/', while the
// catch block only moved focus — a real failure was completely invisible.
// Classify the envelope BEFORE persisting or navigating. Contract:
// src/utils/api-response.ts.
import {
  MutationResult,
  classifyMutationResult,
  isSuccess,
} from '../../../utils/api-response';

// Fixed, outcome-independent copy. Deliberately does NOT echo payload.message /
// payload.details: on this endpoint the backend distinguishes "no such account"
// from "wrong password" in that text, and an unauthenticated sign-in form is
// exactly where an account-enumeration oracle must not be opened. The transport
// variant is separate only because "we could not reach the server" is
// actionable in a different way (retry) than bad credentials.
const SIGN_IN_FAILED_MESSAGE =
  'We could not sign you in. Please check your email and password and try again.';
const SERVER_UNREACHABLE_MESSAGE =
  'We could not reach the server. Please check your connection and try again.';

const isNonEmptyString = (value: unknown): value is string =>
  typeof value === 'string' && value.length > 0;

/**
 * A body-level SUCCESS is not proof of a usable session.
 *
 * `errorCode === '0'` only says the request was accepted; the `data` slot of
 * that same envelope can still be `''`, `null`, or a half-built object. The
 * previous handler destructured it blindly, so:
 *   - `data: null`  threw a TypeError with NO catch and NO alert — a silent
 *     failure on the highest-severity surface in this portal;
 *   - `data: {}` or any partial object persisted the literal string
 *     'undefined' as the access/refresh token and navigated to '/', leaving an
 *     admin "signed in" against a session the API will reject forever.
 *
 * Typed against the `Login` payload so tsc keeps this field list honest if the
 * session contract ever changes, and it FAILS CLOSED: an unknown shape is a
 * failed sign-in, never a session.
 */
const isCompleteSession = (value: unknown): value is LoginSession => {
  if (!value || typeof value !== 'object') return false;

  const session = value as Partial<LoginSession>;

  return (
    isNonEmptyString(session.accessToken) &&
    isNonEmptyString(session.refreshToken) &&
    isNonEmptyString(session.userName)
  );
};

export const Login = () => {
  const [loginTrials, setLoginTrials] = useState(1);
  const [errorMessage, setErrorMessage] = useState('');
  const [login, { isLoading: isLoginLoading }] = useLoginMutation();
  // src/components/Button.tsx accepts only a `type` prop and cannot be
  // disabled (reworking it is out of scope), so submit re-entrancy is guarded
  // in the handler. A ref rather than state on purpose: it flips synchronously,
  // so a second submit that arrives before React has re-rendered — the exact
  // double-click case — is still a no-op.
  const isSubmitInFlight = useRef(false);
  const navigate = useNavigate();
  const { data } = useGetAuthConfigurationQuery();

  const emailValidation = data?.data?.EMAIL_LOGIN_REGEX?.value;

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setFocus,
  } = useForm<LoginData>();

  const { email } = watch();

  // Every failure lands here: one fixed string, no persistence, no navigation,
  // form values kept so a single field can be corrected.
  const failSignIn = (message: string) => {
    setErrorMessage(message);
    setFocus('email');
  };

  const onSubmit: SubmitHandler<LoginData> = async loginData => {
    if (isLoginLoading || isSubmitInFlight.current) return;
    isSubmitInFlight.current = true;

    // loginTrials semantics are untouched: it still counts submits that were
    // actually sent, and still drives the "reset your password instead" hint.
    setLoginTrials(loginTrials + 1);
    setErrorMessage('');

    try {
      // NOT `.unwrap()`. The raw result carries BOTH the body-level envelope
      // and RTK Query's `{ error }` transport shape, which is what the
      // classifier reads; unwrapping throws away the former distinction.
      const response = await login(loginData);

      // The ONLY branch allowed to persist a session or navigate. `'data' in
      // response` is what narrows the union for TypeScript — keep it.
      if (isSuccess(response) && 'data' in response) {
        // Typed as `Login` by the endpoint, but the WIRE decides what actually
        // arrives, so it is re-checked at runtime before anything is written.
        const session: unknown = response.data.data;

        if (isCompleteSession(session)) {
          localStorage.setItem('accessToken', session.accessToken);
          localStorage.setItem('refreshToken', session.refreshToken);
          localStorage.setItem('userName', session.userName);

          navigate('/');
          return;
        }
      }

      // Everything else — BODY_FAILURE (including `data: ''` / `data: null`),
      // TRANSPORT_FAILURE, and a success-coded body carrying an incomplete
      // session — persists nothing, navigates nowhere, and is made visible.
      failSignIn(
        classifyMutationResult(response) === MutationResult.TRANSPORT_FAILURE
          ? SERVER_UNREACHABLE_MESSAGE
          : SIGN_IN_FAILED_MESSAGE
      );
    } catch {
      // Nothing reaches here through a NORMAL login outcome — the trigger is
      // awaited without `.unwrap()`, so both failure bands resolve rather than
      // throw. What reaches here is the unexpected: a malformed result whose
      // `data` slot is absent, an aborted request, a localStorage write the
      // browser refuses (private mode / quota). TICKET-006 left this branch
      // out entirely, which is how a `data: null` body became an unhandled
      // TypeError with the form silently doing nothing. Fail closed and
      // visibly; the generic copy is right because we genuinely do not know
      // what happened, and it still leaks nothing about the account.
      failSignIn(SIGN_IN_FAILED_MESSAGE);
    } finally {
      // Released whichever way the attempt ended, so the form stays usable.
      isSubmitInFlight.current = false;
    }
  };

  return (
    <div className="mx-auto mt-32 max-w-sm 2xl:mt-60">
      <h2 className="mb-3 text-center text-2xl font-bold text-secondary 2xl:text-3xl">
        Sign in
      </h2>
      <p className="mb-7 text-center text-gray-500">
        Use your work email to sign in
      </p>
      <form onSubmit={handleSubmit(onSubmit)}>
        {/* Same inline alert as forget-password in this module — this repo has
            no toast/notifier at all, so the call site owns visibility. */}
        {errorMessage && (
          <div
            role="alert"
            className="mb-5 rounded-lg border border-error bg-error/10 px-4 py-3 text-sm text-error-600"
          >
            {errorMessage}
          </div>
        )}

        <div className="mb-5">
          <Input
            required
            type="text"
            name="email"
            id="email"
            label="Email"
            placeholder="Enter your email"
            register={register}
            prefixIcon={mailImg}
            isError={errors.email?.type === 'pattern'}
            pattern={
              new RegExp(
                emailValidation
                  ? emailValidation.slice(1, emailValidation.length - 1)
                  : '',
                'g'
              )
            }
          />
        </div>

        <div>
          <Input
            required
            type="password"
            name="password"
            id="password"
            label="Password"
            register={register}
          />
        </div>

        <div className="my-6 flex w-full justify-end">
          <Link
            state={email}
            to="/forget-password"
            className="font-bold text-primary"
          >
            Forget password
          </Link>
        </div>

        {loginTrials > 3 && (
          <div className="mt-3 bg-warning p-6">
            <p className="text-sm font-semibold text-secondary">
              It happens! we can help you
            </p>
            <p className="text-sm ">
              Instead of trying more, you can just reset your password by
              clicking on{' '}
              <Link
                to="/forget-password"
                state={email}
                className="text-primary underline"
              >
                Forget Password
              </Link>
            </p>
          </div>
        )}

        <div className="mt-6 w-full">
          <Button type="submit">
            {isLoginLoading ? 'Signing in…' : 'Sign in'}
          </Button>
        </div>
      </form>
    </div>
  );
};
